import type { FundDetail } from "@/data/fund-detail-types";
import { cn } from "@/lib/utils";

export function BufferStrategyStrip({ fund }: { fund: FundDetail }) {
  const cards = [
    {
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          focusable="false"
        >
          <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
        </svg>
      ),
      label: "Reference Asset",
      value: fund.referenceTicker || "\u2014",
      sub: fund.referenceName || "",
      glow: false,
      path: "bufferConfig.referenceTicker bufferConfig.referenceName",
    },
    {
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          focusable="false"
        >
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
      ),
      label: "Downside Buffer",
      value: fund.bufferLevel ? `${fund.bufferLevel.toFixed(2)}%` : "\u2014",
      sub: fund.bufferLevel
        ? fund.bufferLevel >= 100
          ? `Targeted ${fund.bufferLevel.toFixed(2)}% of losses absorbed`
          : fund.bufferStart && fund.bufferStart > 0
            ? `Losses from \u2212${fund.bufferStart.toFixed(2)}% to \u2212${(fund.bufferStart + fund.bufferLevel).toFixed(2)}% absorbed`
            : `First ${fund.bufferLevel.toFixed(2)}% of losses absorbed`
        : "",
      glow: true,
      path: "bufferConfig.bufferLevel",
    },
    {
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          focusable="false"
        >
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 16 14" />
        </svg>
      ),
      label: "Outcome Period",
      value: "Annual",
      sub:
        fund.outcomePeriodStart && fund.outcomePeriodEnd
          ? `${fund.outcomePeriodStart} \u2013 ${fund.outcomePeriodEnd}`
          : "",
      glow: false,
      path: "bufferConfig.outcomePeriodStart bufferConfig.outcomePeriodEnd",
    },
  ];

  return (
    <div
      data-fund-section="bufferConfig"
      className="grid grid-cols-3 max-md:grid-cols-1 border-b border-[var(--etf-border)] gap-px bg-transparent animate-[fadeIn_0.5s_ease_0.1s_both]"
    >
      {cards.map((card, i) => (
        <div
          key={card.label}
          data-fund-field={card.path}
          className={cn(
            "px-7 max-md:px-5 py-6 max-md:py-4 border-r border-[var(--etf-border)] max-md:border-r-0 max-md:border-b max-md:last:border-b-0 last:border-r-0 flex flex-col gap-1 bg-[linear-gradient(to_bottom,transparent_0%,var(--etf-black2)_55%)] hover:bg-[linear-gradient(to_bottom,transparent_0%,var(--etf-black3)_55%)] transition-[background] duration-150 opacity-0 translate-y-[10px]",
            "animate-[bufFadeUp_0.5s_ease_forwards]",
          )}
          style={{ animationDelay: `${i * 0.08}s` }}
        >
          <div className="text-[var(--etf-orange)] opacity-70 mb-0.5 [&>svg]:w-5 [&>svg]:h-5">
            {card.icon}
          </div>
          <div className="font-[family-name:var(--font-geist)] text-[10px] tracking-[0.14em] uppercase text-[var(--etf-white3)]">
            {card.label}
          </div>
          <div
            className={cn(
              "font-[family-name:var(--font-f37bolton)] text-[26px] max-[480px]:text-[22px] text-[var(--etf-white)] tracking-[-0.02em]",
              card.glow && "text-[var(--etf-orange)] [text-shadow:0_0_20px_rgba(255,85,0,0.3)]",
            )}
          >
            {card.value}
          </div>
          <div className="font-[family-name:var(--font-geist)] text-xs text-[var(--etf-white3)] leading-[1.4]">
            {card.sub}
          </div>
        </div>
      ))}
    </div>
  );
}
